'use client';

import type { ReactNode } from 'react';
import { Box, Divider, HStack, VStack } from '@terra/ui-styled-system';
import { BodyStrong, MetaDefault, MonoSmall } from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import { formatCloudProviderLabel } from '@terra/shared-contracts-integrations';
import type { AssetNodeData } from './constants';

const MAX_VISIBLE_OPEN_PORTS = 8;

type TooltipRow = {
  key: string;
  label: string;
  value: ReactNode;
};

type ExternalNetworkGraphNodeTooltipProps = {
  data: AssetNodeData;
  position: { x: number; y: number };
  translationNamespace?: 'externalNetwork';
};

export function ExternalNetworkGraphNodeTooltip({
  data,
  position,
  translationNamespace = 'externalNetwork',
}: ExternalNetworkGraphNodeTooltipProps) {
  const t = useTranslations(`${translationNamespace}.graph.tooltip`);
  const openPorts = data.openPorts ?? [];
  const visibleOpenPorts = openPorts.slice(0, MAX_VISIBLE_OPEN_PORTS);
  const hiddenOpenPortsCount = openPorts.length - visibleOpenPorts.length;
  const rows: TooltipRow[] = [
    ...(visibleOpenPorts.length > 0
      ? [
          {
            key: 'openPorts',
            label: t('openPorts'),
            value: (
              <MonoSmall color="fg.tooltip.default">
                {visibleOpenPorts.join(', ')}
                {hiddenOpenPortsCount > 0
                  ? ` ${t('andMore', { count: hiddenOpenPortsCount })}`
                  : null}
              </MonoSmall>
            ),
          },
        ]
      : []),
    ...(data.cloudProvider
      ? [
          {
            key: 'cloud',
            label: t('cloud'),
            value: (
              <MetaDefault color="fg.tooltip.default">
                {formatCloudProviderLabel({ provider: data.cloudProvider })}
              </MetaDefault>
            ),
          },
        ]
      : []),
    ...(data.signalsCount > 0
      ? [
          {
            key: 'signals',
            label: t('signals'),
            value: (
              <MetaDefault color="fg.tooltip.default">
                {data.signalsCount}
              </MetaDefault>
            ),
          },
        ]
      : []),
  ];

  if (rows.length === 0) {
    return null;
  }

  return (
    <Box
      position="fixed"
      pointerEvents="none"
      zIndex="tooltip"
      bg="bg.tooltip"
      borderWidth="1px"
      borderColor="border.tooltip"
      borderRadius="l1"
      boxShadow="sm"
      px={2.5}
      py={2}
      style={{
        left: position.x,
        top: position.y - 8,
        transform: 'translate(-50%, -100%)',
      }}
    >
      <VStack gap={1.5} alignItems="stretch" minW="160px" maxW="280px">
        <BodyStrong color="fg.tooltip.default" wordBreak="break-all">
          {data.label}
        </BodyStrong>
        <Divider borderColor="border.tooltip" />
        {rows.map((row) => (
          <HStack
            key={row.key}
            gap={3}
            w="full"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <MetaDefault
              color="fg.tooltip.muted"
              whiteSpace="nowrap"
              flexShrink={0}
            >
              {row.label}
            </MetaDefault>
            <Box textAlign="right">{row.value}</Box>
          </HStack>
        ))}
      </VStack>
    </Box>
  );
}
